import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAppContext } from "../context/AppContext";
import {
  helpSnippet,
  loadHelpManifest,
  loadHelpTopic,
  searchHelpTopics,
} from "../services/helpDocsService";
import "../styles/help-docs.css";

const topicIdFromPath = (pathname) => {
  const match = /^\/help\/([^/]+)\/?$/.exec(pathname || "");
  return match ? decodeURIComponent(match[1]) : null;
};

const isModifiedEvent = (event) =>
  event.metaKey || event.altKey || event.ctrlKey || event.shiftKey;

const escapeRegExp = (text) => text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const ancestorsOf = (manifest, id) => {
  const chain = [];
  let parent = manifest?.topics[id]?.parent;
  while (parent) {
    chain.unshift(parent);
    parent = manifest.topics[parent]?.parent;
  }
  return chain;
};

const Highlight = ({ text, query }) => {
  const terms = query.trim().split(/\s+/).filter(Boolean).map(escapeRegExp);
  if (!terms.length) return text;
  const pattern = new RegExp(`(${terms.join("|")})`, "ig");
  return String(text)
    .split(pattern)
    .map((part, i) => (i % 2 ? <mark key={i}>{part}</mark> : part));
};

const ChevronIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M9 6l6 6-6 6"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
    <path d="M20 20l-3.5-3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const BookIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M4 5.5A2.5 2.5 0 016.5 3H20v15H6.5A2.5 2.5 0 004 20.5v-15zM4 20.5A2.5 2.5 0 006.5 23H20v-5"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
    <path d="M8 7.5h8M8 11h6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

/* ------------------------------------------------------------------ */
/* Table of contents tree                                              */
/* ------------------------------------------------------------------ */

const TocNode = ({ node, depth, manifest, activeId, activePath, expanded, onToggle, onOpen }) => {
  const topic = manifest.topics[node.id];
  const hasChildren = node.children.length > 0;
  const isOpen = expanded.has(node.id);
  const isActive = node.id === activeId;

  return (
    <li className="hd-toc-item">
      <div
        className={`hd-toc-row depth-${Math.min(depth, 3)}${isActive ? " active" : ""}${
          !isActive && activePath.has(node.id) ? " in-path" : ""
        }`}
        style={{ paddingLeft: 6 + depth * 14 }}
      >
        {hasChildren ?
          <button
            type="button"
            className={`hd-toc-caret${isOpen ? " open" : ""}`}
            aria-expanded={isOpen}
            aria-label={`${isOpen ? "Collapse" : "Expand"} ${topic.title}`}
            onClick={() => onToggle(node.id)}
          >
            <ChevronIcon />
          </button>
        : <span className="hd-toc-caret-spacer" aria-hidden="true" />}
        <a
          href={`/help/${node.id}`}
          className="hd-toc-link"
          data-toc-id={node.id}
          aria-current={isActive ? "page" : undefined}
          onClick={(event) => {
            if (event.button !== 0 || isModifiedEvent(event)) return;
            event.preventDefault();
            onOpen(node.id);
          }}
        >
          {topic.title}
        </a>
      </div>
      {hasChildren && isOpen && (
        <ul className="hd-toc-list">
          {node.children.map((child) => (
            <TocNode
              key={child.id}
              node={child}
              depth={depth + 1}
              manifest={manifest}
              activeId={activeId}
              activePath={activePath}
              expanded={expanded}
              onToggle={onToggle}
              onOpen={onOpen}
            />
          ))}
        </ul>
      )}
    </li>
  );
};

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

const HelpDocsPage = () => {
  const location = useLocation();
  const { setActiveSection } = useAppContext();
  const topicId = topicIdFromPath(location.pathname);

  const [manifest, setManifest] = useState(null);
  const [manifestError, setManifestError] = useState(false);
  const [topicHtml, setTopicHtml] = useState("");
  const [topicStatus, setTopicStatus] = useState("idle"); // idle | loading | ready | missing | error
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState(() => new Set());
  const [navOpen, setNavOpen] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  const rootRef = useRef(null);
  const treeRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    loadHelpManifest()
      .then((data) => {
        if (!cancelled) setManifest(data);
      })
      .catch(() => {
        if (!cancelled) setManifestError(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const topic = manifest && topicId ? manifest.topics[topicId] : null;

  useEffect(() => {
    if (!manifest || !topicId) {
      setTopicStatus("idle");
      return undefined;
    }
    if (!manifest.topics[topicId]) {
      setTopicStatus("missing");
      return undefined;
    }
    if (manifest.topics[topicId].empty) {
      setTopicHtml("");
      setTopicStatus("ready");
      return undefined;
    }
    let cancelled = false;
    setTopicStatus("loading");
    loadHelpTopic(topicId)
      .then((html) => {
        if (cancelled) return;
        setTopicHtml(html);
        setTopicStatus("ready");
      })
      .catch(() => {
        if (!cancelled) setTopicStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, [manifest, topicId]);

  const activePath = useMemo(
    () => new Set(manifest && topicId ? ancestorsOf(manifest, topicId) : []),
    [manifest, topicId],
  );

  // Open the branch that leads to the current topic, start it at the top of
  // the page, and bring its row into view in the contents tree.
  useEffect(() => {
    if (!manifest || !topicId || !manifest.topics[topicId]) return undefined;
    setExpanded((prev) => {
      const next = new Set(prev);
      ancestorsOf(manifest, topicId).forEach((id) => next.add(id));
      if (manifest.topics[topicId].children.length) next.add(topicId);
      return next;
    });
    setNavOpen(false);
    rootRef.current?.closest(".dashboard-content")?.scrollTo({ top: 0 });

    const frame = requestAnimationFrame(() => {
      const tree = treeRef.current;
      if (!tree) return;
      const selector =
        typeof CSS !== "undefined" && CSS.escape ? CSS.escape(topicId) : topicId;
      const row = tree.querySelector(`[data-toc-id="${selector}"]`);
      if (!row) return;
      // Measure against the tree on screen (offsetTop would be relative to the
      // positioned row) and only scroll when the row is actually out of view,
      // so clicking a visible entry leaves the tree where the reader put it.
      const treeRect = tree.getBoundingClientRect();
      const rowRect = row.getBoundingClientRect();
      if (rowRect.top < treeRect.top || rowRect.bottom > treeRect.bottom) {
        tree.scrollTop += rowRect.top - treeRect.top - tree.clientHeight / 3;
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [manifest, topicId]);

  // "/" jumps to search from anywhere on the page, as on most docs sites.
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key !== "/" || isModifiedEvent(event)) return;
      const target = event.target;
      if (
        target instanceof HTMLElement &&
        (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName))
      ) {
        return;
      }
      event.preventDefault();
      searchRef.current?.focus();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  useEffect(() => {
    if (!lightbox) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setLightbox(null);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [lightbox]);

  const openTopic = useCallback(
    (id) => {
      setQuery("");
      if (manifest?.topics[id]?.children.length) {
        setExpanded((prev) => new Set(prev).add(id));
      }
      if (id !== topicId) setActiveSection("help-topic", { topicId: id });
    },
    [manifest, topicId, setActiveSection],
  );

  const openHome = useCallback(() => {
    setQuery("");
    setActiveSection("help-docs");
  }, [setActiveSection]);

  const toggleNode = useCallback((id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const linkProps = (id) => ({
    href: `/help/${id}`,
    onClick: (event) => {
      if (event.button !== 0 || isModifiedEvent(event)) return;
      event.preventDefault();
      openTopic(id);
    },
  });

  const onContentClick = (event) => {
    const link = event.target.closest("a[data-topic]");
    if (link) {
      if (event.button !== 0 || isModifiedEvent(event)) return;
      event.preventDefault();
      openTopic(link.getAttribute("data-topic"));
      return;
    }
    const image = event.target.closest("figure img");
    if (image) setLightbox({ src: image.currentSrc || image.src, alt: image.alt });
  };

  const trimmedQuery = query.trim();
  const searching = trimmedQuery.length >= 2;
  const results = useMemo(
    () => (searching ? searchHelpTopics(manifest, trimmedQuery) : []),
    [manifest, searching, trimmedQuery],
  );

  const position = manifest && topicId ? manifest.order.indexOf(topicId) : -1;
  const prevId = position > 0 ? manifest.order[position - 1] : null;
  const nextId =
    position >= 0 && position < manifest.order.length - 1 ?
      manifest.order[position + 1]
    : null;

  const pathTitles = (id) =>
    ancestorsOf(manifest, id)
      .map((a) => manifest.topics[a].title)
      .join(" › ");

  /* ---------------- views ---------------- */

  const renderSearch = () => (
    <section className="hd-article hd-search" aria-live="polite">
      <p className="hd-eyebrow">Search</p>
      <h1 className="hd-title">Results for “{trimmedQuery}”</h1>
      <p className="hd-lead">
        {results.length === 0 ?
          "No topics match. Try fewer or different words."
        : `${results.length}${results.length === 50 ? "+" : ""} topic${
            results.length === 1 ? "" : "s"
          } found.`}
      </p>
      <ol className="hd-results">
        {results.map((id) => {
          const t = manifest.topics[id];
          const crumbs = pathTitles(id);
          const snippet = helpSnippet(t.text, trimmedQuery);
          return (
            <li key={id}>
              <a className="hd-result" {...linkProps(id)}>
                {crumbs && <span className="hd-result-path">{crumbs}</span>}
                <span className="hd-result-title">
                  <Highlight text={t.title} query={trimmedQuery} />
                </span>
                {snippet && (
                  <span className="hd-result-snippet">
                    <Highlight text={snippet} query={trimmedQuery} />
                  </span>
                )}
              </a>
            </li>
          );
        })}
      </ol>
    </section>
  );

  const renderChildCards = (ids) => (
    <div className="hd-cards">
      {ids.map((id) => {
        const t = manifest.topics[id];
        const blurb =
          t.text ? helpSnippet(t.text, "", 70)
          : t.children.length ?
            t.children
              .slice(0, 4)
              .map((c) => manifest.topics[c].title)
              .join(" · ")
          : "";
        return (
          <a key={id} className="hd-card" {...linkProps(id)}>
            <span className="hd-card-title">{t.title}</span>
            {blurb && <span className="hd-card-blurb">{blurb}</span>}
            {t.children.length > 0 && (
              <span className="hd-card-meta">
                {t.children.length} topic{t.children.length === 1 ? "" : "s"}
              </span>
            )}
          </a>
        );
      })}
    </div>
  );

  const renderHome = () => (
    <>
      <section className="hd-hero">
        <p className="hd-eyebrow">Help &amp; Docs</p>
        <h1 className="hd-title">{manifest.title}</h1>
        <p className="hd-lead">
          Installation, everyday use and troubleshooting for every part of the
          system — from signing in to process optimization and the 3D
          environment. Browse the contents or press <kbd>/</kbd> to search.
        </p>
        <div className="hd-hero-actions">
          <a className="hd-primary-btn" {...linkProps(manifest.order[0])}>
            Start reading
            <ChevronIcon />
          </a>
          <span className="hd-hero-count">
            {manifest.order.length} topics · {manifest.tree.length} chapters
          </span>
        </div>
      </section>
      <section className="hd-home-contents">
        <h2 className="hd-section-heading">Contents</h2>
        <div className="hd-chapters">
          {manifest.tree.map((node, index) => {
            const t = manifest.topics[node.id];
            const shown = node.children.slice(0, 6);
            return (
              <div key={node.id} className="hd-chapter">
                <a className="hd-chapter-title" {...linkProps(node.id)}>
                  <span className="hd-chapter-num">{index + 1}</span>
                  {t.title}
                </a>
                {shown.length > 0 ?
                  <ul className="hd-chapter-list">
                    {shown.map((child) => (
                      <li key={child.id}>
                        <a {...linkProps(child.id)}>{manifest.topics[child.id].title}</a>
                      </li>
                    ))}
                    {node.children.length > shown.length && (
                      <li className="hd-chapter-more">
                        <a {...linkProps(node.id)}>
                          +{node.children.length - shown.length} more
                        </a>
                      </li>
                    )}
                  </ul>
                : t.text && (
                    <p className="hd-chapter-blurb">{helpSnippet(t.text, "", 60)}</p>
                  )
                }
              </div>
            );
          })}
        </div>
      </section>
    </>
  );

  const renderTopic = () => {
    if (topicStatus === "missing" || !topic) {
      return (
        <section className="hd-article hd-state">
          <h1 className="hd-title">Topic not found</h1>
          <p className="hd-lead">
            This page is not part of the current user manual. It may have been
            renamed when the manual was updated.
          </p>
          <button type="button" className="hd-primary-btn" onClick={openHome}>
            Go to contents
          </button>
        </section>
      );
    }

    const ancestors = ancestorsOf(manifest, topicId);
    const parentTitle =
      ancestors.length ? manifest.topics[ancestors[ancestors.length - 1]].title : "User Manual";

    return (
      <>
        <nav className="hd-breadcrumb" aria-label="Breadcrumb">
          <a href="/help" onClick={(e) => {
            if (e.button !== 0 || isModifiedEvent(e)) return;
            e.preventDefault();
            openHome();
          }}>
            User Manual
          </a>
          {ancestors.map((id) => (
            <React.Fragment key={id}>
              <span className="hd-breadcrumb-sep" aria-hidden="true">›</span>
              <a {...linkProps(id)}>{manifest.topics[id].title}</a>
            </React.Fragment>
          ))}
          <span className="hd-breadcrumb-sep" aria-hidden="true">›</span>
          <span aria-current="page">{topic.title}</span>
        </nav>

        <article className="hd-article">
          <p className="hd-eyebrow">{parentTitle}</p>
          <h1 className="hd-title">{topic.title}</h1>

          {topicStatus === "loading" && (
            <div className="hd-skeleton" aria-label="Loading topic">
              <span />
              <span />
              <span />
            </div>
          )}
          {topicStatus === "error" && (
            <p className="hd-error">
              This topic could not be loaded. Check your connection and try again.
            </p>
          )}
          {topicStatus === "ready" && topicHtml && (
            // Content is generated from the bundled manual by
            // scripts/import-help-docs.mjs, never from user input.
            <div
              className="hd-content"
              onClick={onContentClick}
              dangerouslySetInnerHTML={{ __html: topicHtml }}
            />
          )}

          {topic.children.length > 0 && (
            <section className="hd-children">
              <h2 className="hd-section-heading">In this section</h2>
              {renderChildCards(topic.children)}
            </section>
          )}
        </article>

        <nav className="hd-pager" aria-label="Previous and next topics">
          {prevId ?
            <a className="hd-pager-link prev" {...linkProps(prevId)}>
              <span className="hd-pager-label">Previous</span>
              <span className="hd-pager-title">{manifest.topics[prevId].title}</span>
            </a>
          : <span />}
          {nextId && (
            <a className="hd-pager-link next" {...linkProps(nextId)}>
              <span className="hd-pager-label">Next</span>
              <span className="hd-pager-title">{manifest.topics[nextId].title}</span>
            </a>
          )}
        </nav>
      </>
    );
  };

  let main;
  if (manifestError) {
    main = (
      <section className="hd-article hd-state">
        <h1 className="hd-title">User manual unavailable</h1>
        <p className="hd-lead">
          The help content could not be loaded. Refresh the page to try again.
        </p>
      </section>
    );
  } else if (!manifest) {
    main = (
      <div className="hd-article">
        <div className="hd-skeleton" aria-label="Loading user manual">
          <span />
          <span />
          <span />
        </div>
      </div>
    );
  } else if (searching) {
    main = renderSearch();
  } else if (topicId) {
    main = renderTopic();
  } else {
    main = renderHome();
  }

  return (
    <div className="page-container help-docs-page" ref={rootRef}>
      <div className="hd-layout">
        <aside className={`hd-nav${navOpen ? " open" : ""}`} aria-label="User manual">
          <div className="hd-nav-head">
            <a
              href="/help"
              className="hd-brand"
              onClick={(e) => {
                if (e.button !== 0 || isModifiedEvent(e)) return;
                e.preventDefault();
                openHome();
              }}
            >
              <span className="hd-brand-icon">
                <BookIcon />
              </span>
              <span className="hd-brand-text">
                <span className="hd-brand-title">User Manual</span>
                <span className="hd-brand-sub">3D-DET Crystal System</span>
              </span>
            </a>
            <div className="hd-search-box">
              <SearchIcon />
              <input
                ref={searchRef}
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    setQuery("");
                    e.currentTarget.blur();
                  }
                }}
                placeholder="Search the manual"
                aria-label="Search the user manual"
                disabled={!manifest}
              />
              {!query && <kbd aria-hidden="true">/</kbd>}
            </div>
            <button
              type="button"
              className="hd-nav-toggle"
              aria-expanded={navOpen}
              onClick={() => setNavOpen((v) => !v)}
            >
              {navOpen ? "Hide contents" : "Show contents"}
            </button>
          </div>
          <div className="hd-toc" ref={treeRef}>
            {manifest && (
              <ul className="hd-toc-list root">
                {manifest.tree.map((node) => (
                  <TocNode
                    key={node.id}
                    node={node}
                    depth={0}
                    manifest={manifest}
                    activeId={topicId}
                    activePath={activePath}
                    expanded={expanded}
                    onToggle={toggleNode}
                    onOpen={openTopic}
                  />
                ))}
              </ul>
            )}
          </div>
        </aside>

        <main className="hd-main">{main}</main>
      </div>

      {lightbox && (
        <div
          className="hd-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={lightbox.alt || "Screenshot"}
          onClick={() => setLightbox(null)}
        >
          <button
            type="button"
            className="hd-lightbox-close"
            aria-label="Close"
            autoFocus
            onClick={() => setLightbox(null)}
          >
            ×
          </button>
          <img src={lightbox.src} alt={lightbox.alt} />
          {lightbox.alt && <p className="hd-lightbox-caption">{lightbox.alt}</p>}
        </div>
      )}
    </div>
  );
};

export default HelpDocsPage;
